import React, { useState, useEffect, useContext } from "react";
import {
    Box, Button, Chip, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, CircularProgress, IconButton, Tooltip,
    TextField, MenuItem, InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddLinkIcon from "@mui/icons-material/AddLink";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useSnackbar } from "notistack";
import Header from "../../components/Header";
import { SocketContext } from "../../socket";
import { getAuthToken } from "../../utils/authToken";
import v2Api from "../../utils/v2Api";
import GenerateLinkDialog from "./GenerateLinkDialog";
import EditTakerDialog from "./EditTakerDialog";

// Lifecycle status → label + MUI Chip color (theme-aware).
const STATUS_META = {
    generated: { label: "Generated", color: "default" },
    opened: { label: "Opened", color: "info" },
    in_progress: { label: "In progress", color: "warning" },
    completed: { label: "Completed", color: "success" },
    expired: { label: "Expired", color: "error" },
};

const FILTERS = ["all", "generated", "opened", "in_progress", "completed", "expired"];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

const PlacementTestProgress = () => {
    const { enqueueSnackbar } = useSnackbar();
    const socket = useContext(SocketContext);

    const [loading, setLoading] = useState(true);
    const [takers, setTakers] = useState([]);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [testFilter, setTestFilter] = useState("all");
    const [genOpen, setGenOpen] = useState(false);
    const [editTaker, setEditTaker] = useState(null);

    // Drill-in reuses the canonical v2 reviewer (full fidelity: math, images, FRQ).
    // It renders purely from /sessions/:sid; the course uuid + course_test id only
    // drive its back button, so the host values are safe to pass.
    const openReview = (taker, session) => {
        const courseUuid = taker.v2_course_test?.course?.uuid;
        const courseTestId = taker.v2_course_test?.id;
        if (!courseUuid || !courseTestId || !session?.id) {
            enqueueSnackbar("Result not available yet.", { variant: "warning" });
            return;
        }
        window.open(`/classrooms/${courseUuid}/test-v2/${courseTestId}/result/${session.id}`, "_blank");
    };

    const takerLink = (taker) => `${window.location.origin}/placement/${taker.token}`;
    const copyLink = (taker) => {
        navigator.clipboard.writeText(takerLink(taker));
        enqueueSnackbar("링크를 복사했습니다.", { variant: "success" });
    };

    const fetchTakers = async (status, { silent = false } = {}) => {
        try {
            if (!silent) setLoading(true);
            const res = await v2Api.get("/placement/links", status && status !== "all" ? { status } : undefined);
            setTakers(res.data?.data || []);
        } catch (err) {
            console.error("Failed to load placement takers:", err);
            if (!silent) enqueueSnackbar("진행상황을 불러오지 못했습니다.", { variant: "error" });
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        fetchTakers(filter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    // Connect the shared socket (autoConnect:false) with auth, once.
    useEffect(() => {
        if (!socket) return;
        socket.auth = { token: getAuthToken() };
        socket.connect();
    }, [socket]);

    // Live updates: when a taker changes, patch just that one row in place
    // (no spinner, no full refetch). Filtered views can change membership, so
    // those fall back to a silent refetch.
    useEffect(() => {
        if (!socket) return;
        const onPlacement = async (payload) => {
            const id = payload?.taker_id;
            if (!id || filter !== "all") { fetchTakers(filter, { silent: true }); return; }
            try {
                const res = await v2Api.get(`/placement/links/${id}`);
                const row = res.data?.data;
                if (!row) return;
                setTakers((prev) => {
                    const i = prev.findIndex((t) => t.id === row.id);
                    if (i === -1) return [row, ...prev];
                    const next = prev.slice();
                    next[i] = row;
                    return next;
                });
            } catch { /* ignore */ }
        };
        const onFrqGraded = () => fetchTakers(filter, { silent: true }); // no taker id on this event
        socket.on("global.placementUpdated", onPlacement);
        socket.on("global.apTestFRQGraded", onFrqGraded);
        return () => {
            socket.off("global.placementUpdated", onPlacement);
            socket.off("global.apTestFRQGraded", onFrqGraded);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socket, filter]);

    const handleGenerated = () => {
        setGenOpen(false);
        fetchTakers(filter);
    };

    // Distinct tests present in the current list (for the Test filter dropdown).
    const testOptions = (() => {
        const m = new Map();
        takers.forEach((t) => {
            const test = t.v2_course_test?.v2_test;
            if (test && !m.has(test.id)) m.set(test.id, test.title);
        });
        return [...m.entries()].map(([id, title]) => ({ id, title }));
    })();

    // Client-side search + test filter over the (status-filtered) list.
    const term = search.trim().toLowerCase();
    const filtered = takers.filter((t) => {
        const test = t.v2_course_test?.v2_test;
        if (testFilter !== "all" && test?.id !== testFilter) return false;
        if (!term) return true;
        const hay = [t.email, t.name, t.phone, test?.title].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(term);
    });

    return (
        <Box m="20px">
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Header
                    title="Placement Test 진행상황"
                    subtitle="Generate placement-test links for prospects and track who's taken them."
                />
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<AddLinkIcon />}
                    onClick={() => setGenOpen(true)}
                >
                    Generate Link
                </Button>
            </Box>

            {/* Status filter pills + search + test filter */}
            <Box display="flex" gap="10px" mb="16px" flexWrap="wrap" alignItems="center">
                {FILTERS.map((f) => {
                    const active = filter === f;
                    return (
                        <Chip
                            key={f}
                            label={f === "all" ? "All" : STATUS_META[f].label}
                            onClick={() => setFilter(f)}
                            variant={active ? "filled" : "outlined"}
                            color={active ? "primary" : "default"}
                            sx={{ cursor: "pointer" }}
                        />
                    );
                })}
                <Box flexGrow={1} />
                <TextField
                    size="small" placeholder="Search email / name / phone…"
                    value={search} onChange={(e) => setSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                    sx={{ minWidth: 260 }}
                />
                <TextField
                    select size="small" label="Test" value={testFilter}
                    onChange={(e) => setTestFilter(e.target.value)} sx={{ minWidth: 180 }}
                >
                    <MenuItem value="all">All tests</MenuItem>
                    {testOptions.map((t) => <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>)}
                </TextField>
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" p="40px">
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "action.hover" }}>
                                {["Taker", "Notes", "Test", "Status", "Link", "Generated", "Results", "View Test"].map((h) => (
                                    <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ color: "text.secondary", py: 4 }}>
                                        {takers.length === 0 ? "No placement links yet." : "No matching takers."}
                                    </TableCell>
                                </TableRow>
                            )}
                            {filtered.map((t) => {
                                const meta = STATUS_META[t.status] || STATUS_META.generated;
                                const session = t.v2_student_test_session;
                                const test = t.v2_course_test?.v2_test;
                                return (
                                    <TableRow key={t.id} hover sx={{ cursor: "pointer" }} onClick={() => setEditTaker(t)}>
                                        <TableCell>
                                            <Typography variant="body2"><b>Email:</b> {t.email || "N/A"}</Typography>
                                            <Typography variant="body2"><b>Name:</b> {t.name || "N/A"}</Typography>
                                            <Typography variant="body2"><b>Phone:</b> {t.phone || "N/A"}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 220 }}>
                                            {t.notes
                                                ? <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{t.notes}</Typography>
                                                : <Typography color="text.secondary">—</Typography>}
                                        </TableCell>
                                        <TableCell>{test?.title || "—"}</TableCell>
                                        <TableCell><Chip size="small" label={meta.label} color={meta.color} /></TableCell>
                                        <TableCell>
                                            <Tooltip title={takerLink(t)}>
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); copyLink(t); }}>
                                                    <ContentCopyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Preview (read-only — won't affect the taker's status)">
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); window.open(`${takerLink(t)}?preview=1`, "_blank"); }}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>{fmtDate(t.createdAt)}</TableCell>
                                        <TableCell>
                                            {(t.grading_status === "grading" || t.grading_status === "failed") && (
                                                <Chip
                                                    size="small" sx={{ mb: 0.5 }}
                                                    label={t.grading_status === "grading" ? "Grading…" : "Grading error"}
                                                    color={t.grading_status === "grading" ? "warning" : "error"}
                                                />
                                            )}
                                            {t.grading_status !== "grading" && (t.section_scores && t.section_scores.length > 0 ? (() => {
                                                const graded = t.section_scores.some((s) => s.score != null);
                                                const tScore = t.section_scores.reduce((a, s) => a + (s.score || 0), 0);
                                                const tMax = t.section_scores.reduce((a, s) => a + (s.total || 0), 0);
                                                return (
                                                    <>
                                                        <Typography variant="body2" sx={{ borderBottom: "1px solid", borderColor: "divider", pb: 0.25, mb: 0.25 }}>
                                                            <b>Total:</b> {graded ? `${tScore}/${tMax}` : "N/A"}
                                                        </Typography>
                                                        {t.section_scores.map((s, i) => (
                                                            <Typography key={i} variant="body2">
                                                                <b>{s.name}:</b> {s.score == null ? "N/A" : `${s.score}/${s.total}`}
                                                            </Typography>
                                                        ))}
                                                    </>
                                                );
                                            })() : (
                                                <Typography color="text.secondary">N/A</Typography>
                                            ))}
                                        </TableCell>
                                        <TableCell>
                                            {session?.id ? (
                                                <Tooltip title="View test session — answers & right/wrong">
                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); openReview(t, session); }}>
                                                        <OpenInNewIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Typography color="text.secondary">N/A</Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <GenerateLinkDialog open={genOpen} onClose={() => setGenOpen(false)} onGenerated={handleGenerated} />
            <EditTakerDialog
                taker={editTaker}
                open={!!editTaker}
                onClose={() => setEditTaker(null)}
                onSaved={() => { setEditTaker(null); fetchTakers(filter); }}
            />
        </Box>
    );
};

export default PlacementTestProgress;
